import type { AlertFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength, when } from '@/utils/validx-util'
import { required, validationContext } from 'validx'

/**
 * Reasons an admin can pick for reopening a job.
 */
export const reopenReasons: string[] = [
  'Amendment / correction of a filed return',
  'IRS / state notice',
  'Missing deliverable',
  'Client questions after close',
  'Filing not finished / closed too early',
  'Client resumes service',
  'Deliverable rework (partner format)',
  'Estimated payments / vouchers',
  'Billing / admin adjustment',
  'New scope added after close',
  'Other',
]

/**
 * View Model for the Reopen Job as Admin dialog.
 */
export class ReopenJobAsAdminDialogViewModel {
  /**
   * Hold the maximum length of reason field.
   */
  private readonly _reasonLength = {
    reason: { max: 100 },
  }

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The selected reason for reopening the job.
   */
  @observable accessor reasonOption = ''

  /**
   * The reason typed in when "Other" is selected.
   */
  @observable accessor otherReason = ''

  /**
   * Helper text for reason input field.
   */
  readonly reasonHelpText = `Why are you reopening?
    This text is public and can be seen by both Client and Pros.`

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    reasonOption: [required('A reason is required.')],
    otherReason: [
      when(() => this.isOtherReason, required('A reason is required.')),
      mustHaveMaxLength(
        this._reasonLength.reason.max,
        `Reason must be less than ${this._reasonLength.reason.max} characters.`,
      ),
    ],
  })

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    private readonly _newAlert: AlertFactory,
  ) {}

  /**
   * Whether the "Other" reason is selected.
   */
  @computed
  get isOtherReason() {
    return this.reasonOption === 'Other'
  }

  /**
   * The reason for reopening the job, as sent to the API.
   */
  @computed
  get reason() {
    return this.isOtherReason ? this.otherReason : this.reasonOption
  }

  /**
   * Whether the reopen cannot be started.
   */
  @computed
  get isReopenDisabled() {
    return this.reopenJobAsAdmin.pending || !this._hasReason
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.reasonOption = ''
    this.otherReason = ''

    this.reopenJobAsAdmin.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Reopens job as admin.
   */
  reopenJobAsAdmin = task.resolved(async () => {
    if (!this.validate()) {
      return
    }
    const alert = this._newAlert()
      .withState('pending')
      .saying('Reopening Job', `Reopening ${this._job.name}...`)
      .autoDismiss()
      .show()

    try {
      await this._jobStore.reopenJobAsAdmin(this._job.id, this.reason)
    } catch (err) {
      alert.dismiss()
      throw err
    }

    alert
      .withState('success')
      .saying('Success!', `${this._job.name} is reopened.`)
      .autoDismiss()

    this.modal.close()
  })

  /**
   * Shows Reopen Job as Admin Dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }

  @computed
  private get _hasReason() {
    return this.isOtherReason
      ? this.otherReason.trim().length > 0
      : this.reasonOption !== ''
  }
}
